<script setup lang="ts">
  import pluralize from 'pluralize';
  import { computed } from 'vue';
  import { RouterLink, useRoute } from 'vue-router';

  import { DunningStateAction } from '@stuut/api/models';
  import type { Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';

  import TaskBriefDisableContact from '@/components/tasks/TaskBriefDisableContact.vue';
  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';

  const props = defineProps<{
    task: Task;
    groupId?: string;
  }>();

  const emit = defineEmits<{
    (e: 'openContactEditor'): void;
    (e: 'selectRelatedTasks'): void;
  }>();

  const route = useRoute();

  const pauseStateInfo = computed(() => {
    const d = (props.task?.acknowledgeTaskData?.data as any) || undefined;

    return (
      d?.pause_state_update_info ||
      (d?.old_state || d?.new_state
        ? { new_state: d?.new_state, old_state: d?.old_state }
        : undefined)
    );
  });
</script>

<template>
  <div class="flex flex-col gap-y-4">
    <template v-if="task?.is_group">
      <div class="space-y-2.5">
        <div class="flex items-center justify-between">
          <strong class="text-muted-foreground"> Task brief: </strong>
        </div>

        <template v-if="task?.isExpectsComplete">
          <p>
            The following invoices were mentioned but we could not determine a promises payment date
          </p>

          <p>
            Please review each task and either set a payment promise date for the invoice or dismiss
            the subtask
          </p>
        </template>

        <template v-else-if="task?.isExpectsAcknowledge">
          <p>
            Review and acknowledge (mark complete) the following payment promise dates set by our
            agent.
          </p>

          <p>
            Click edit to update any dates that may be inaccurate. If an invoice was marked by
            mistake or needs to be reverted for any reason simply clear the date and then dismiss
            the task or flag it as inaccurate.
          </p>
        </template>
      </div>
    </template>

    <template v-else>
      <div class="space-y-2.5">
        <TaskBriefDisableContact
          v-if="task?.isDisableContact"
          @open-contact-editor="emit('openContactEditor')"
        />

        <template v-else-if="task?.isPauseStateUpdate">
          <p class="font-bold text-muted-foreground">Task brief:</p>

          <p>
            <strong>{{ task?.label }}</strong>
          </p>

          <p>
            The customer state changed from
            <strong>
              {{ pauseStateInfo?.old_state?.name }}
            </strong>
            to
            <strong> {{ pauseStateInfo?.new_state?.name }} </strong>.
          </p>
          <p>
            Dunning is now
            <strong
              >{{
                pauseStateInfo?.new_state?.action === DunningStateAction.OUTREACH_PAUSE
                  ? 'paused'
                  : 'enabled'
              }}.</strong
            >
          </p>
        </template>

        <template v-else-if="task?.isSmsSendFailure">
          <p class="font-bold text-muted-foreground">Task brief:</p>

          <p>
            <strong>{{ task?.label }}</strong>
          </p>
        </template>

        <template v-else-if="task?.classificationReason">
          <div class="flex items-center justify-between">
            <strong class="text-muted-foreground"> Task brief: </strong>

            <RouterLink
              v-if="task?.dispute_id && !task?.dispute?.isProposed"
              :to="{
                name: 'disputes.dispute',
                params: { id: task?.dispute_id },
              }"
            >
              <Button class="font-semibold text-xs" variant="link" size="inline">
                Dispute

                <template v-if="task?.disputeNumber"> #{{ task?.disputeNumber }} </template>
              </Button>
            </RouterLink>

            <div v-if="groupId" class="flex items-center gap-x-2">
              <RouterLink
                class="text-2xs font-bold text-blue hover:underline"
                :to="{
                  query: {
                    ...route.query,
                    task_drawer_id: groupId,
                  },
                }"
              >
                <span>view group</span>
              </RouterLink>
            </div>
          </div>

          <p>
            <strong>{{ task?.label }}</strong>
          </p>

          <p>{{ task?.classificationReason }}</p>
          <p>
            {{ task?.classificationRecommendation }}
          </p>
        </template>
      </div>
    </template>

    <TaskReplyWarning
      class="text-xs text-muted-foreground"
      :task="task"
      :show-any-task-type="true"
    />

    <Button
      v-if="task?.nonArchivedRelatedTasks?.length"
      class="w-fit"
      variant="ghost"
      size="inline"
      @click="emit('selectRelatedTasks')"
    >
      <i class="fas fa-grid-2-plus" />
      <span>{{ pluralize('related task', task?.nonArchivedRelatedTasks?.length, true) }}</span>
    </Button>
  </div>
</template>
